import React, { SyntheticEvent, useCallback, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  useBillingInformationsByOrga,
  BillingMethod,
  CreditBalance,
} from '@app/api';

import { useToast, LargeModaleWithLeftPane, InformationBox } from '@app/core';

import { BillingInformationForm } from './BillingInformationForm';
import { BillingMethodForm } from './BillingMethodForm';
import { TitleWithIcon } from '../TitleWithIcon';
import { PaymentMethodStep } from './PaymentMethodStep';
import { BillingModalButtons } from './BillingModalButtons';
import { PriceTotals } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  invoiceCallBox: {
    marginTop: theme.marginBase * 3,
    marginBottom: theme.marginBase * 3,
  },
  inContainerInfoBox: {
    marginBottom: theme.marginBase * 3,
  },
  container: {
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  },
  scrollContainer: {
    padding: theme.marginBase * 3,
    maxHeight: '100%',
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: () => any;
  organizationId: string;
  specialInformation?: string;
  priceInfos?: React.ReactNode;
  creditBalance?: CreditBalance;
  priceTotals?: PriceTotals;
  onShouldApplyVatChange?: (val: boolean) => any;
  defaultBillingMethod: BillingMethod;
  defaultPaymentMethodId?: string;
  onBillingMethodChange?: (billingMethod: BillingMethod) => any;
  onFinish?: (res: {
    billingMethod: BillingMethod;
    paymentMethodId?: string;
  }) => any;
  requireInvoiceFeature?: boolean;
  context?: string;
  sessionId?: string;
}

export const BillingModal = ({
  isOpen,
  onRequestClose,
  organizationId,
  specialInformation,
  priceInfos,
  creditBalance,
  priceTotals,
  onShouldApplyVatChange,
  defaultBillingMethod,
  defaultPaymentMethodId,
  onBillingMethodChange,
  onFinish,
  context,
  requireInvoiceFeature,
  sessionId,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const toast = useToast();

  const [activeStep, setActiveStep] = useState(0);
  const { data: billingInformation } =
    useBillingInformationsByOrga(organizationId);

  const [billingMethod, setBillingMethod] =
    useState<BillingMethod>(defaultBillingMethod);

  const stepsCount = billingMethod === BillingMethod.CREDIT_CARD ? 3 : 2;
  const isLastStep = stepsCount === activeStep + 1;

  const handleNext = useCallback(
    async (e?: SyntheticEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (isLastStep) {
        if (onFinish) {
          await onFinish({
            billingMethod,
          });
        } else {
          onRequestClose();
        }
        setActiveStep(0);
        return;
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    },
    [isLastStep, onRequestClose, setActiveStep, toast, billingMethod, onFinish],
  );

  const onBillingMethodSubmit = useCallback(
    async (billingMethod: BillingMethod) => {
      setBillingMethod(billingMethod);
      if (onBillingMethodChange) {
        await onBillingMethodChange(billingMethod);
      }
      handleNext();
    },
    [handleNext, setBillingMethod, onBillingMethodChange],
  );
  const onPaymentMethodSubmit = useCallback(
    async (paymentMethodId?: string) => {
      if (onFinish) {
        await onFinish({
          billingMethod,
          paymentMethodId,
        });
      } else {
        onRequestClose();
      }
      setActiveStep(0);
    },
    [onRequestClose, setActiveStep, onFinish],
  );

  if (!billingInformation) {
    return null;
  }

  return (
    <LargeModaleWithLeftPane
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      paneTitle={t('billing.pricePane.title', { context })}
      paneContent={priceInfos}
    >
      <div className={classes.container}>
        <div className={classes.scrollContainer}>
          {!!specialInformation && (
            <InformationBox
              text={specialInformation}
              className={classes.inContainerInfoBox}
            />
          )}

          {activeStep === 0 && (
            <>
              <TitleWithIcon
                icon="ri-file-list-3-line"
                iconColor="blue"
                title={t('billing.billingMethod.title')}
              />
              <BillingMethodForm
                onSuccess={onBillingMethodSubmit}
                submitButton={
                  <BillingModalButtons
                    currentStep={activeStep}
                    stepsCount={stepsCount}
                    setActiveStep={setActiveStep}
                  />
                }
                setBillingMethod={setBillingMethod}
                billingMethod={billingMethod}
                requireInvoiceFeature={requireInvoiceFeature}
                context={context}
                creditBalance={creditBalance}
                priceTotals={priceTotals}
              />
            </>
          )}
          {activeStep === 1 && (
            <>
              <TitleWithIcon
                icon="ri-briefcase-3-line"
                iconColor="green"
                title={t('billing.informations.title')}
              />
              <BillingInformationForm
                billingInformation={billingInformation}
                onSuccess={() => handleNext()}
                submitButton={
                  <BillingModalButtons
                    currentStep={activeStep}
                    stepsCount={stepsCount}
                    setActiveStep={setActiveStep}
                    onSkip={handleNext}
                  />
                }
                onShouldApplyVatChange={onShouldApplyVatChange}
              />
            </>
          )}
          {activeStep === 2 && billingMethod === BillingMethod.CREDIT_CARD && (
            <>
              <TitleWithIcon
                icon="ri-bank-card-line"
                iconColor="green"
                title={t('billing.paymentMethod.title')}
              />
              <PaymentMethodStep
                organizationId={organizationId}
                onSuccess={onPaymentMethodSubmit}
                sessionId={sessionId}
                submitButton={
                  <BillingModalButtons
                    currentStep={activeStep}
                    stepsCount={stepsCount}
                    setActiveStep={setActiveStep}
                  />
                }
                defaultPaymentMethodId={defaultPaymentMethodId}
              />
            </>
          )}
        </div>
      </div>
    </LargeModaleWithLeftPane>
  );
};
